import React, { useEffect, useState, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import {
  IconAreas,
  IconDashboard,
  IconMenu,
  IconUser,
  IconWallet,
} from '../icons/Icons';
import './AppLayout.css';

interface AppLayoutProps {
  children: ReactNode;
  /** Отключить нижний колонтитул (например, для страниц с полноэкранным контентом). */
  hideFooter?: boolean;
}

/**
 * Основной каркас личного кабинета: боковое меню + верхняя панель + контент.
 *
 * Layout только оборачивает страницы — маршруты, права доступа и данные
 * остаются в существующих компонентах без изменений.
 */
const AppLayout: React.FC<AppLayoutProps> = ({ children, hideFooter = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Меню закрывается при переходе на другой раздел
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Блокируем прокрутку страницы, пока открыт мобильный drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  return (
    <div className="app-shell">
      <Sidebar mobileOpen={mobileOpen} onCloseMobile={() => setMobileOpen(false)} />

      <div className="app-shell__main">
        <Header onOpenMenu={() => setMobileOpen(true)} />

        <main className="app-shell__content">{children}</main>

        {!hideFooter && <Footer />}
      </div>

      <nav className="mobile-nav" aria-label="Быстрая навигация">
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            `mobile-nav__item${isActive ? ' mobile-nav__item--active' : ''}`
          }
        >
          <IconDashboard size={20} />
          <span>Дашборд</span>
        </NavLink>
        <NavLink
          to="/dashboard"
          className={`mobile-nav__item${
            location.pathname.startsWith('/area') ? ' mobile-nav__item--active' : ''
          }`}
        >
          <IconAreas size={20} />
          <span>Участки</span>
        </NavLink>
        <NavLink
          to="/balance"
          className={({ isActive }) =>
            `mobile-nav__item${isActive ? ' mobile-nav__item--active' : ''}`
          }
        >
          <IconWallet size={20} />
          <span>Баланс</span>
        </NavLink>
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            `mobile-nav__item${isActive ? ' mobile-nav__item--active' : ''}`
          }
        >
          <IconUser size={20} />
          <span>Профиль</span>
        </NavLink>
        <button
          type="button"
          className="mobile-nav__item"
          onClick={() => setMobileOpen(true)}
        >
          <IconMenu size={20} />
          <span>Меню</span>
        </button>
      </nav>
    </div>
  );
};

export default AppLayout;
